import { createThunk, model, Model } from 'mst-collection';
import { getRoot, types } from 'mobx-state-tree';
import format from 'date-fns/format';

import {
  Api,
  ISignInBodyRequest,
  ITokensResponse,
  IForgotPasswordRequest,
  ISetNewPasswordRequest,
  ISignUpBodyRequest,
  IVerifySignUpDataBodyRequest,
  IUpdatePasswordRequest,
  IRestoreAccountBodyRequest,
  SocketApi,
} from '@api';
import { useRoot } from './useRoot';
import { trimData } from '@utils';
import {
  AnalyticsService,
  NotificationsService,
  PersistService,
  TokenService,
} from '@services';
import type { RootModelType } from './RootModel';
import {
  SignUpDraftModel,
  SignUpDraft,
} from './Viewer/SignUpDraftModel';

interface ISignInParams extends ISignInBodyRequest {
  rememberMe: boolean;
}

type ForgotPasswordParamsType = IForgotPasswordRequest;
type SetNewPasswordParamsType = ISetNewPasswordRequest;
type UpdatePasswordParamsType = IUpdatePasswordRequest;
type RestoreAccountParamsType = Omit<
  IRestoreAccountBodyRequest,
  'restoreDate'
>;

interface ISignUpParams
  extends Omit<ISignUpBodyRequest, 'signUpDate'> {}

type VerifySignUpDataType = IVerifySignUpDataBodyRequest;

export class Auth extends Model({
  /**
   * null means that status is not defined yet
   */
  isAuthorized: types.maybeNull(types.boolean),
  signUpDraft: types.maybeNull(SignUpDraftModel),
  isFirstLogin: false,
}) {
  authorize() {
    getRoot<RootModelType>(this).app.incrementAuthCount();

    this.isAuthorized = true;
  }

  unauthorize() {
    this.isAuthorized = false;
  }

  saveSignUpDraft(draft: SignUpDraft) {
    this.signUpDraft = draft;
  }

  clearSignUpDraft() {
    this.signUpDraft = null;
  }

  setIsFirstLogin(value: boolean) {
    this.isFirstLogin = value;
  }

  signIn = createThunk(({ rememberMe, ...params }: ISignInParams) => {
    return async function signIn(this: Auth) {
      const data = trimData(params);

      const res = await Api.Auth.signIn(data);

      this.setIsFirstLogin(res.data.isFirstLogin);

      await this.saveTokens(res.data, rememberMe);

      const viewer = getRoot<RootModelType>(this).viewer;

      await viewer.fetch.run();

      AnalyticsService.trackUserParams({
        email: data.email,
      });
    };
  });

  signOut = createThunk(() => {
    return async function signOut(this: Auth) {
      PersistService.persist.purge();
      getRoot<RootModelType>(this).reset();

      const token = await NotificationsService.getToken();

      if (token) {
        // If request is failed this will be safe
        await getRoot<RootModelType>(this)
          .viewer.removePushNotificationsToken.run({ token })
          ?.catch(() => {});
      }

      AnalyticsService.clear();

      await SocketApi.disconnect();

      // If request is failed this will be safe
      await Api.Auth.signOut().catch(() => {});

      TokenService.clear();
    };
  });

  deleteAccount = createThunk(() => {
    return async function deleteAccount(this: Auth) {
      const deleteDate = format(
        new Date(),
        "yyyy-MM-dd'T'hh:mm:ssxxx",
      );

      await Api.Auth.deleteAccount(deleteDate);

      this.signOut.run();
      this.unauthorize();
    };
  });

  restoreAccount = createThunk((params: RestoreAccountParamsType) => {
    return async function restoreAccount(this: Auth) {
      const data = trimData(params);
      const restoreDate = format(
        new Date(),
        "yyyy-MM-dd'T'hh:mm:ssxxx",
      );

      await Api.Auth.restoreAccount({
        ...data,
        restoreDate,
      });
    };
  });

  forgotPassword = createThunk((params: ForgotPasswordParamsType) => {
    return async function forgotPassword(this: Auth) {
      const data = trimData(params);

      await Api.Auth.forgotPassword(data);
    };
  });

  setNewPassword = createThunk((params: SetNewPasswordParamsType) => {
    return async function setNewPassword(this: Auth) {
      const data = trimData(params);

      await Api.Auth.setNewPassword(data);
    };
  });

  updatePassword = createThunk((params: UpdatePasswordParamsType) => {
    return async function updatePassword(this: Auth) {
      const data = trimData(params);

      const res = await Api.Auth.updatePassword(data);

      this.updateTokens(res.data);
    };
  });

  signUp = createThunk((params: ISignUpParams) => {
    return async function signUp(this: Auth) {
      const data = trimData(params);
      const signUpDate = new Date();

      const res = await Api.Auth.signUp({
        ...data,
        signUpDate: signUpDate.toISOString(),
      });

      AnalyticsService.trackUserParams({
        signUpDate,
      });

      return res.data;
    };
  });

  /**
   * Verify Sign Up data on BE after Sign Up steps
   * E.g. after the first step, we verify if the user already exists
   * with a specific email before going to the next step
   */
  verifySignUpData = createThunk((params: VerifySignUpDataType) => {
    return async function verifySignUpData(this: Auth) {
      const data = trimData(params);

      await Api.Auth.verifySignUpData(data);
    };
  });

  /**
   * This is needed only when you go through registration process
   * via Share video link (with invite).
   * After using getReferrerVideo request once all next attempts
   * will give { "id": null } in result
   */
  getInviteVideo = createThunk(() => {
    return async function getInviteVideo(this: Auth) {
      const res = await Api.Auth.getReferrerVideo();

      return res.data;
    };
  });

  renewTokens = createThunk(() => {
    return async function renewTokens(this: Auth) {
      await Api.refreshTokenRenewalController.renewToken();
    };
  });

  async saveTokens(tokens: ITokensResponse, rememberMe: boolean) {
    await TokenService.set(
      {
        accessToken: tokens.token,
        refreshToken: tokens.refreshToken,
        refreshTokenExpiryTime: tokens.refreshTokenExpiryTime,
      },
      {
        sessionOnly: !rememberMe,
      },
    );
  }

  async updateTokens(tokens: ITokensResponse) {
    await this.saveTokens(tokens, TokenService.isRememberMe());
  }
}

export const AuthModel = model(Auth);

export function useAuthModel() {
  const root = useRoot();

  return root.auth;
}
